import { dialogTemplate } from './dialog-template';
import { inlineTemplate } from './inline-template';
import {
  ClicktermDialogMode,
  ClicktermDialogTranslations,
  ClicktermInlineStyleOptions,
  ClickwrapCustomizationSettingsResult,
  ClickwrapTemplateContent
} from '../models';
import { ClickwrapError } from '../utils';
import { getBodyCss, getFontUrl, getInlineCss, getShadowCss } from './shadow-styles';
import { defaultDialogTranslations } from '../translations';
import { isAllowedContentTag } from './content-policy';
import {
  normalizeInlineTheme,
  reportInlineThemeIssues,
  rewriteContentDeclaration,
  sanitizeCssValue,
} from './inline-theme';

const inlineShadowRoots = new WeakMap<HTMLElement, ShadowRoot>();

/**
 * Base href for the "Powered by ClickTerm" brand link, UTM-tagged for analytics
 * attribution (CT-4). Must stay in sync with the default `href` hardcoded on the
 * `.ct-w-brand` anchor in `dialog-template.ts`. `utm_campaign` is appended at
 * runtime by {@link buildBrandHref} once the host `appId` is known.
 */
export const BRAND_BASE_HREF = 'https://clickterm.com/?utm_source=widget&utm_medium=powered-by';

/**
 * Single source of truth for the brand anchor href. Both the static template
 * default and the runtime `showDefaultBrand()` path route through this so they
 * cannot diverge. When `appId` is available it is appended as a URL-encoded
 * `utm_campaign`; otherwise the campaign param is omitted entirely.
 */
export function buildBrandHref(appId?: string): string {
  if (!appId) {
    return BRAND_BASE_HREF;
  }
  return `${BRAND_BASE_HREF}&utm_campaign=${encodeURIComponent(appId)}`;
}

export class Renderer {

  private readonly P = 'ct-w-';
  private readonly inlineText = {
    checkboxLabelText: 'I agree to the [ClickwrapTitle].',
  };
  private readonly allowedStyleProperties = new Set([
    'background',
    'background-color',
    'border',
    'border-bottom',
    'border-color',
    'border-left',
    'border-radius',
    'border-right',
    'border-top',
    'color',
    'display',
    'font-family',
    'font-size',
    'font-style',
    'font-weight',
    'height',
    'letter-spacing',
    'line-height',
    'list-style',
    'list-style-position',
    'list-style-type',
    'margin',
    'margin-bottom',
    'margin-left',
    'margin-right',
    'margin-top',
    'max-width',
    'padding',
    'padding-bottom',
    'padding-left',
    'padding-right',
    'padding-top',
    'text-align',
    'text-decoration',
    'text-decoration-color',
    'text-decoration-thickness',
    'text-underline-offset',
    'white-space',
    'width',
    'word-break',
    'word-spacing'
  ]);
  private readonly dialogTemplate: HTMLTemplateElement;
  private readonly inlineTemplate: HTMLTemplateElement;
  private shadowHost: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private bodyStyleInjected = false;
  private fontStylesheetInjected = false;

  constructor(
    private readonly document: Document,
    private readonly appId?: string,
  ) {
    this.dialogTemplate = this.createDialogTemplate();
    this.inlineTemplate = this.createInlineTemplate();
  }

  private ensureModalShadowRoot(): ShadowRoot {
    if (this.shadowRoot) {
      return this.shadowRoot;
    }

    this.shadowHost = this.document.createElement('div');
    this.document.body.appendChild(this.shadowHost);

    this.shadowRoot = this.attachClosedShadowRoot(this.shadowHost, getShadowCss(), true);

    return this.shadowRoot;
  }

  private createInlineShadowRoot(host: HTMLElement): ShadowRoot {
    const existingShadowRoot = inlineShadowRoots.get(host);
    if (existingShadowRoot) {
      return existingShadowRoot;
    }

    const shadowRoot = this.attachClosedShadowRoot(host, getInlineCss());
    inlineShadowRoots.set(host, shadowRoot);
    return shadowRoot;
  }

  private attachClosedShadowRoot(
    host: HTMLElement,
    cssText: string,
    injectBodyStyle = false
  ): ShadowRoot {
    let shadowRoot: ShadowRoot;
    try {
      shadowRoot = host.attachShadow({ mode: 'closed' });
    } catch (error) {
      throw new ClickwrapError('Failed to create closed Shadow DOM.', error);
    }

    this.ensureFontStylesheet();

    const style = this.document.createElement('style');
    style.textContent = cssText;
    shadowRoot.appendChild(style);

    if (injectBodyStyle && !this.bodyStyleInjected) {
      const bodyCss = getBodyCss();
      if (bodyCss) {
        const bodyStyle = this.document.createElement('style');
        bodyStyle.setAttribute('data-ct-w-body-styles', '');
        bodyStyle.textContent = bodyCss;
        this.document.head.appendChild(bodyStyle);
        this.bodyStyleInjected = true;
      }
    }

    return shadowRoot;
  }

  private ensureFontStylesheet(): void {
    if (this.fontStylesheetInjected) {
      return;
    }

    const fontUrl = getFontUrl();
    if (!fontUrl) {
      return;
    }

    const existing = this.document.head.querySelector(
      `link[data-ct-w-font-stylesheet][href="${fontUrl}"]`
    );
    if (existing) {
      this.fontStylesheetInjected = true;
      return;
    }

    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontUrl;
    link.setAttribute('data-ct-w-font-stylesheet', '');
    this.document.head.appendChild(link);
    this.fontStylesheetInjected = true;
  }

  createModal(
    _: ClickwrapTemplateContent,
    translations: ClicktermDialogTranslations = defaultDialogTranslations,
    customizations?: ClickwrapCustomizationSettingsResult,
    options: ModalRenderOptions = {}
  ): ModalRef {
    const {
      title,
      content,
      templateLastUpdatedTimestamp,
      clickwrapTemplateVersion,
      clickwrapTemplateVersionMinor
    } = _;
    const modal = this.document.createElement('div');
    modal.classList.add(`${this.P}modal`);
    modal.setAttribute('role', 'presentation');

    const container = this.document.createElement('div');
    container.classList.add(`${this.P}modal-container`);
    container.setAttribute('role', 'dialog');
    container.setAttribute('aria-modal', 'true');
    container.setAttribute('aria-label', title);

    const dialog = this.cloneTemplate();
    this.applyLogoCustomization(dialog, customizations);
    this.applyThemeCustomizations(modal, customizations);

    const checkboxLabelText = this.replaceAgreementPlaceholders(translations.checkboxLabelText, title);
    const checkboxAriaLabel = this.buildCheckboxAriaLabel(title, checkboxLabelText);
    const checkboxLegalText = this.replaceAgreementPlaceholders(
      translations.checkboxDescription,
      title
    );
    const scrollTitleText = this.replaceAgreementPlaceholders(
      translations.scrollTextTitle,
      title
    );
    const scrollLegalText = this.replaceAgreementPlaceholders(
      translations.scrollTextSubtitle,
      title
    );
    dialog.infoBannerText.textContent = scrollTitleText;

    dialog.title.textContent = title;
    this.renderModalAcknowledgement(dialog, _, checkboxLabelText, options.renderInlineAcknowledgement === true);
    dialog.ackInput.setAttribute('aria-label', checkboxAriaLabel);
    dialog.ackInput.checked = false;
    dialog.bannerLegal.textContent = scrollLegalText;
    dialog.ackLegal.textContent = checkboxLegalText;
    dialog.accept.textContent = translations.accept;
    dialog.decline.textContent = translations.decline;
    dialog.scrollLabel.textContent = translations.scroll;

    const subtitle = this.buildSubtitle(templateLastUpdatedTimestamp);
    const versionLabel = Number.isFinite(clickwrapTemplateVersion) && Number.isFinite(clickwrapTemplateVersionMinor)
      ? `${translations.version} ${clickwrapTemplateVersion}.${clickwrapTemplateVersionMinor}`
      : '';
    if (dialog.subtitle) {
      const subtitleParts = [];
      if (subtitle && subtitle.length > 0) {
        subtitleParts.push(`${translations.publishedOn}: ${subtitle}`);
      }
      if (versionLabel) {
        subtitleParts.push(versionLabel);
      }
      if (subtitleParts.length > 0) {
        dialog.subtitle.textContent = subtitleParts.join(' | ');
      }
    }

    dialog.scrollContent.appendChild(this.sanitizeContent(content));
    dialog.accept.disabled = true;
    dialog.accept.classList.add('is-disabled');
    dialog.accept.classList.remove('is-primary');

    const mode: ClicktermDialogMode = customizations?.agreementMode ?? 'checkbox';
    const renderInlineAcknowledgement = options.renderInlineAcknowledgement === true;
    const enforceScroll = !renderInlineAcknowledgement && mode === 'scroll';
    const enforceCheckbox = renderInlineAcknowledgement || mode === 'checkbox';

    dialog.infoBanner.classList.toggle('is-hidden', enforceCheckbox);
    dialog.scrollHint.classList.toggle('is-hidden', !enforceScroll);
    dialog.ackRow.classList.toggle('is-hidden', !enforceCheckbox);
    dialog.scrollArea.classList.toggle('is-checkbox-mode', enforceCheckbox);

    container.appendChild(dialog.fragment);
    modal.appendChild(container);
    this.ensureModalShadowRoot().appendChild(modal);

    window.setTimeout(() => {
      this.document.body.classList.add(`${this.P}modal-open`);
      modal.classList.add(`${this.P}visible`);
      this.startScrollHintBounce(dialog.scrollHint);
    }, 10);

    const state = {
      scrolled: !enforceScroll,
      agreed: !enforceCheckbox
    };

    if (enforceScroll) {
      dialog.scrollArea.addEventListener('scroll', () => this.syncModalState(dialog, state, enforceScroll));
    }

    dialog.ackInput.addEventListener('change', () => {
      if (!enforceCheckbox) {
        return;
      }
      state.agreed = dialog.ackInput.checked;
      this.syncModalState(dialog, state, enforceScroll);
    });

    this.syncModalState(dialog, state, enforceScroll);

    return {
      root: modal,
      close: () => {
        return this.closeModal(modal);
      }
    };
  }

  createReadOnlyModal(
    _: ClickwrapTemplateContent,
    translations: ClicktermDialogTranslations = defaultDialogTranslations,
    customizations?: ClickwrapCustomizationSettingsResult,
    options: ReadOnlyModalOptions = {}
  ): ModalRef {
    const { title, content, eventAcceptedTimestamp, templateLastUpdatedTimestamp, clickwrapTemplateVersion, clickwrapTemplateVersionMinor } = _;
    const modal = this.document.createElement('div');
    modal.classList.add(`${this.P}modal`);
    modal.setAttribute('role', 'presentation');

    const container = this.document.createElement('div');
    container.classList.add(`${this.P}modal-container`);
    container.setAttribute('role', 'dialog');
    container.setAttribute('aria-modal', 'true');
    container.setAttribute('aria-label', title);

    const dialog = this.cloneTemplate();
    this.applyLogoCustomization(dialog, customizations);
    this.applyThemeCustomizations(modal, customizations);
    dialog.title.textContent = title;
    if (dialog.subtitle) {
      if (options.subtitleMode === 'published') {
        const subtitleParts: string[] = [];
        const publishedDate = this.buildSubtitle(templateLastUpdatedTimestamp);
        if (publishedDate) {
          subtitleParts.push(`${translations.publishedOn}: ${publishedDate}`);
        }
        const versionLabel = Number.isFinite(clickwrapTemplateVersion) && Number.isFinite(clickwrapTemplateVersionMinor)
          ? `${translations.version} ${clickwrapTemplateVersion}.${clickwrapTemplateVersionMinor}`
          : '';
        if (versionLabel) {
          subtitleParts.push(versionLabel);
        }
        if (subtitleParts.length > 0) {
          dialog.subtitle.textContent = subtitleParts.join(' | ');
        }
      } else {
        const subtitle = this.buildSubtitle(eventAcceptedTimestamp);
        dialog.subtitle.textContent = `${translations.acceptedAt}: ${subtitle}`;
      }
    }
    dialog.infoBanner.classList.add('is-hidden');
    dialog.scrollHint.classList.add('is-hidden');
    dialog.ackRow.classList.add('is-hidden');
    dialog.scrollContent.appendChild(this.sanitizeContent(content));
    dialog.decline.classList.add('is-not-visible');
    dialog.decline.textContent = translations.decline;
    dialog.scrollLabel.textContent = translations.scroll;
    dialog.accept.disabled = false;
    dialog.accept.textContent = translations.close;
    dialog.accept.classList.remove('is-disabled');
    dialog.accept.classList.add('is-primary');
    dialog.scrollArea.classList.add('is-at-end');

    container.appendChild(dialog.fragment);
    modal.appendChild(container);
    this.ensureModalShadowRoot().appendChild(modal);

    window.setTimeout(() => {
      this.document.body.classList.add(`${this.P}modal-open`);
      modal.classList.add(`${this.P}visible`);
    }, 10);

    return {
      root: modal,
      close: () => {
        return this.closeModal(modal);
      }
    };
  }

  createInline(
    container: HTMLElement,
    content: ClickwrapTemplateContent,
    inlineStyle?: ClicktermInlineStyleOptions,
    linkModalContext?: LinkModalContext,
  ): InlineRef {
    const shadowRoot = this.createInlineShadowRoot(container);
    shadowRoot.querySelector(`.${this.P}inline`)?.remove();
    const inline = this.cloneInlineTemplate();
    const checkboxLabelText = this.replaceAgreementPlaceholders(this.inlineText.checkboxLabelText, content.title);

    inline.checkboxInput.checked = false;
    inline.checkboxInput.disabled = false;
    inline.checkboxInput.setAttribute('aria-label', checkboxLabelText);
    inline.content.appendChild(this.sanitizeContent(content.inlineContent!, { inlineMarkRewrite: true }));
    inline.root.classList.toggle('is-locked', inline.checkboxInput.disabled);
    this.applyInlineStyleCustomizations(inline.root, inlineStyle);

    if (linkModalContext) {
      inline.root.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        const anchor = target.closest('a');
        if (!anchor) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();

        const modal = this.createReadOnlyModal(
          content,
          linkModalContext.translations,
          linkModalContext.customizations,
          { subtitleMode: 'published' },
        );
        const okButton = modal.root.querySelector('.ct-w-btn.btn-accept') as HTMLButtonElement | null;
        if (okButton) {
          okButton.addEventListener('click', async () => {
            okButton.setAttribute('disabled', 'disabled');
            okButton.classList.add('loading');
            await modal.close();
          });
        }
      });
    }

    shadowRoot.appendChild(inline.fragment);

    return {
      root: inline.root,
      checkbox: inline.checkboxInput,
      setLocked: (locked: boolean) => {
        inline.checkboxInput.disabled = locked;
        inline.root.classList.toggle('is-locked', locked);
      }
    };
  }

  private applyInlineStyleCustomizations(
    root: HTMLElement,
    inlineStyle?: ClicktermInlineStyleOptions,
  ): void {
    if (!inlineStyle) return;
    const { declarations, issues } = normalizeInlineTheme(inlineStyle);
    reportInlineThemeIssues(issues);
    if (!declarations.length) {
      return;
    }

    const themeId = `ct-w-inline-theme-${Math.random().toString(36).slice(2, 10)}`;
    root.setAttribute('data-ct-w-inline-theme', themeId);
    const style = this.document.createElement('style');
    style.textContent = `[data-ct-w-inline-theme="${themeId}"] { ${declarations.join('; ')}; }`;
    root.prepend(style);
  }

  private buildSubtitle(timestamp?: string): string {
    if (!timestamp) {
      return '';
    }
    const formatted = this.formatLastUpdatedDate(timestamp);
    if (!formatted) {
      return '';
    }
    return formatted;
  }

  private renderModalAcknowledgement(
    dialog: DialogElements,
    content: ClickwrapTemplateContent,
    checkboxLabelText: string,
    renderInlineAcknowledgement: boolean,
  ): void {
    dialog.ackField.classList.toggle('is-multiline', renderInlineAcknowledgement);
    dialog.ackLabel.classList.toggle('is-multiline', renderInlineAcknowledgement);

    if (!renderInlineAcknowledgement) {
      dialog.ackLabel.textContent = checkboxLabelText;
      return;
    }

    const plainText = this.extractPlainTextContent(content.inlineContent ?? content.content);
    dialog.ackLabel.textContent = plainText || checkboxLabelText;
  }

  private replaceAgreementPlaceholders(text: string, title: string): string {
    return text
      .replace(/\[Agreement Name\]/g, title)
      .replace(/\[ClickwrapTitle\]/g, title);
  }

  private buildCheckboxAriaLabel(title: string, fallbackText?: string): string {
    const normalizedFallback = fallbackText?.trim();
    if (normalizedFallback) {
      return normalizedFallback;
    }
    return `I agree to the ${title}.`;
  }

  private extractPlainTextContent(content: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const blockTags = new Set([
      'address', 'article', 'aside', 'blockquote', 'caption', 'div', 'dl', 'dt', 'dd', 'fieldset',
      'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr',
      'li', 'main', 'nav', 'ol', 'p', 'pre', 'section', 'table', 'tbody', 'td', 'tfoot', 'th',
      'thead', 'tr', 'ul',
    ]);
    let result = '';

    const appendNewline = (): void => {
      if (!result || result.endsWith('\n')) {
        return;
      }
      result = `${result.trimEnd()}\n`;
    };

    const appendText = (value: string): void => {
      const normalized = value.replace(/\s+/g, ' ').trim();
      if (!normalized) {
        return;
      }
      if (result && !result.endsWith('\n') && !result.endsWith(' ') && !/^[,.;:!?)]/.test(normalized)) {
        result += ' ';
      }
      result += normalized;
    };

    const walk = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        appendText(node.textContent ?? '');
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const element = node as HTMLElement;
      const tag = element.tagName.toLowerCase();
      if (tag === 'style' || tag === 'script' || tag === 'noscript') {
        return;
      }
      if (tag === 'br') {
        appendNewline();
        return;
      }

      const isBlock = blockTags.has(tag);
      if (isBlock) {
        appendNewline();
      }

      element.childNodes.forEach(walk);

      if (isBlock) {
        appendNewline();
      }
    };

    doc.body.childNodes.forEach(walk);
    return result
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  private formatLastUpdatedDate(timestamp: string): string | null {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const day = `${date.getUTCDate()}`.padStart(2, '0');
    const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  static onAll(elements: NodeListOf<Element>, event: string, callback: (e: Event) => void, options?: boolean | AddEventListenerOptions): void {
    elements.forEach(e => e.addEventListener(event, callback, options));
  }

  private syncModalState(dialog: DialogElements, state: { scrolled: boolean; agreed: boolean }, enforceScroll: boolean): void {
    const hasOverflow = dialog.scrollArea.scrollHeight > dialog.scrollArea.clientHeight + 1;
    const atBottom = this.isScrolledToEnd(dialog.scrollArea);
    if (enforceScroll) {
      state.scrolled = state.scrolled || atBottom || !hasOverflow;
    }
    const canAccept = state.scrolled && state.agreed;
    dialog.accept.disabled = !canAccept;
    dialog.accept.classList.toggle('is-disabled', dialog.accept.disabled);
    dialog.accept.classList.toggle('is-primary', !dialog.accept.disabled);
    dialog.scrollHint.classList.toggle('is-hidden', !enforceScroll || atBottom || !hasOverflow);
    dialog.scrollArea.classList.toggle('is-at-end', atBottom || !hasOverflow);
  }

  private closeModal(modal: HTMLElement): Promise<void> {
    this.document.body.classList.remove(`${this.P}modal-open`);
    modal.classList.remove(`${this.P}visible`);
    return new Promise<void>(resolve => {
      window.setTimeout(() => {
        modal.remove();
        if (this.shadowRoot) {
          const remaining = this.shadowRoot.querySelectorAll(`.${this.P}modal`);
          if (remaining.length === 0) {
            this.document.head.querySelector('style[data-ct-w-body-styles]')?.remove();
            this.bodyStyleInjected = false;
            this.document.head.querySelector('link[data-ct-w-font-stylesheet]')?.remove();
            this.fontStylesheetInjected = false;
            this.shadowHost?.remove();
            this.shadowHost = null;
            this.shadowRoot = null;
          }
        }
        resolve();
      }, 200);
    });
  }

  private createDialogTemplate(): HTMLTemplateElement {
    const tpl = this.document.createElement('template');
    tpl.innerHTML = dialogTemplate.trim();
    return tpl;
  }

  private createInlineTemplate(): HTMLTemplateElement {
    const tpl = this.document.createElement('template');
    tpl.innerHTML = inlineTemplate.trim();
    return tpl;
  }

  private cloneTemplate(): DialogElements {
    const fragment = this.dialogTemplate.content.cloneNode(true) as DocumentFragment;
    const dialog = fragment.querySelector(`.${this.P}dialog`) as HTMLElement;
    const title = dialog.querySelector(`.${this.P}title`) as HTMLElement;
    const subtitle = dialog.querySelector(`.${this.P}subtitle`) as HTMLElement | null;
    const brand = dialog.querySelector(`.${this.P}brand`) as HTMLAnchorElement;
    const brandLogo = dialog.querySelector(`.${this.P}brand-logo`) as HTMLImageElement;
    const scrollArea = dialog.querySelector(`.${this.P}scroll-area`) as HTMLElement;
    const scrollContent = dialog.querySelector(`.${this.P}scroll-content`) as HTMLElement;
    const scrollHint = dialog.querySelector(`.${this.P}scroll-hint`) as HTMLElement;
    const scrollLabel = dialog.querySelector(`.${this.P}scroll-label`) as HTMLElement;
    const accept = dialog.querySelector(`.${this.P}btn.btn-accept`) as HTMLButtonElement;
    const decline = dialog.querySelector(`.${this.P}btn.btn-decline`) as HTMLButtonElement;
    const infoBanner = dialog.querySelector(`.${this.P}info-banner`) as HTMLElement;
    const infoBannerText = dialog.querySelector(`.${this.P}banner-text`) as HTMLElement;
    const bannerLegal = dialog.querySelector(`.${this.P}banner-legal`) as HTMLElement;
    const ackRow = dialog.querySelector(`.${this.P}ack`) as HTMLDivElement;
    const ackLegal = dialog.querySelector(`.${this.P}ack-legal`) as HTMLElement;
    const ackField = dialog.querySelector(`.${this.P}ack-field`) as HTMLDivElement;
    const ackInput = dialog.querySelector(`.${this.P}ack-input`) as HTMLInputElement;
    const ackLabel = dialog.querySelector(`.${this.P}ack-label`) as HTMLElement;

    return {
      fragment,
      dialog,
      title,
      subtitle,
      brand,
      brandLogo,
      scrollArea,
      scrollContent,
      scrollHint,
      scrollLabel,
      infoBanner,
      infoBannerText,
      bannerLegal,
      ackRow,
      ackLegal,
      ackField,
      ackInput,
      ackLabel,
      accept,
      decline
    };
  }

  private cloneInlineTemplate(): InlineTemplateElements {
    const fragment = this.inlineTemplate.content.cloneNode(true) as DocumentFragment;
    const root = fragment.querySelector(`.${this.P}inline`) as HTMLElement;
    const checkboxInput = root.querySelector(`.${this.P}ack-input`) as HTMLInputElement;
    const content = root.querySelector(`.${this.P}scroll-content`) as HTMLElement;

    return {
      fragment,
      root,
      checkboxInput,
      content
    };
  }

  private sanitizeContent(content: string, options: ContentSanitizationOptions = {}): DocumentFragment {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const fragment = this.document.createDocumentFragment();
    const resolvedScopeSelector = options.scopeSelector ?? `.${this.P}scroll-content`;
    const allowedAttributes = new Set(['href', 'title', 'aria-label', 'role', 'colspan', 'rowspan', 'scope', 'alt', 'src', 'target', 'style', 'start', 'value']);

    const walk = (node: Node, parent: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        parent.appendChild(this.document.createTextNode(node.textContent ?? ''));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const element = node as HTMLElement;
      const tag = element.tagName.toLowerCase();

      if (!isAllowedContentTag(tag)) {
        element.childNodes.forEach(child => walk(child, parent));
        return;
      }

      if (tag === 'style') {
        const styleContent = this.sanitizeStyleElement(
          element.textContent ?? '',
          resolvedScopeSelector,
          options.inlineMarkRewrite === true,
        );
        if (styleContent) {
          const styleEl = this.document.createElement('style');
          styleEl.textContent = styleContent;
          parent.appendChild(styleEl);
        }
        return;
      }

      const clone = this.document.createElement(tag);
      Array.from(element.attributes).forEach(attr => {
        const name = attr.name.toLowerCase();
        if (name.startsWith('on')) {
          return;
        }
        if (name === 'style') {
          const safeStyle = this.sanitizeInlineStyle(attr.value, {
            inlineMarkRewrite: options.inlineMarkRewrite === true,
            tagName: tag,
          });
          if (safeStyle) {
            clone.setAttribute('style', safeStyle);
          }
          return;
        }
        if (!allowedAttributes.has(name)) {
          return;
        }
        if ((name === 'href' && tag === 'a') || (name === 'src' && tag === 'img')) {
          if (!this.isSafeUrl(attr.value, tag === 'img')) {
            return;
          }
          clone.setAttribute(attr.name, attr.value);
          if (tag === 'a') {
            clone.setAttribute('rel', 'noreferrer noopener');
            clone.setAttribute('target', '_blank');
          }
          return;
        }
        clone.setAttribute(attr.name, attr.value);
      });

      element.childNodes.forEach(child => walk(child, clone));
      parent.appendChild(clone);
    };

    doc.body.childNodes.forEach(child => walk(child, fragment));
    return fragment;
  }

  private sanitizeStyleElement(
    styleText: string,
    scopeSelector: string,
    inlineMarkRewrite: boolean,
  ): string | null {
    const cleaned = styleText.replace(/\/\*[\s\S]*?\*\//g, '').trim();
    if (!cleaned) {
      return null;
    }
    const scoped = this.scopeCss(cleaned, scopeSelector, inlineMarkRewrite);
    return scoped.trim() || null;
  }

  private sanitizeInlineStyle(value: string, options: DeclarationSanitizationOptions): string | null {
    const safe = this.sanitizeDeclarationBlock(value, options);
    return safe ? safe : null;
  }

  private scopeCss(css: string, scopeSelector: string, inlineMarkRewrite: boolean): string {
    const result: string[] = [];
    let index = 0;

    const nextRuleStart = (start: number): number => css.indexOf('{', start);

    while (index < css.length) {
      const braceStart = nextRuleStart(index);
      if (braceStart === -1) {
        break;
      }

      const selectorText = css.slice(index, braceStart).trim();
      const braceEnd = this.findMatchingBrace(css, braceStart);
      if (braceEnd === -1) {
        break;
      }

      const blockContent = css.slice(braceStart + 1, braceEnd).trim();

      if (!selectorText) {
        index = braceEnd + 1;
        continue;
      }

      if (selectorText.startsWith('@media')) {
        const nested = this.scopeCss(blockContent, scopeSelector, inlineMarkRewrite);
        if (nested.trim()) {
          result.push(`${selectorText} { ${nested} }`);
        }
        index = braceEnd + 1;
        continue;
      }

      if (selectorText.startsWith('@')) {
        index = braceEnd + 1;
        continue;
      }

      const declarations = this.sanitizeDeclarationBlock(blockContent, { inlineMarkRewrite });
      const scopedSelectors = this.scopeSelectorList(selectorText, scopeSelector);

      if (declarations && scopedSelectors) {
        result.push(`${scopedSelectors} { ${declarations} }`);
      }

      index = braceEnd + 1;
    }

    return result.join(' ');
  }

  private scopeSelectorList(selectorList: string, scopeSelector: string): string {
    const scoped = selectorList.split(',')
      .map(raw => raw.trim())
      .filter(Boolean)
      .map(selector => {
        if (selector === 'body' || selector === 'html') {
          return scopeSelector;
        }
        if (selector.startsWith(scopeSelector)) {
          return selector;
        }
        return `${scopeSelector} ${selector}`;
      });

    return scoped.join(', ');
  }

  private sanitizeDeclarationBlock(
    block: string,
    options: DeclarationSanitizationOptions = {},
  ): string {
    const declarations = block.split(';')
      .map(dec => dec.trim())
      .filter(Boolean);

    const safeDeclarations = declarations.flatMap(dec => {
      const [property, ...valueParts] = dec.split(':');
      if (!property || !valueParts.length) {
        return null;
      }
      const name = property.trim().toLowerCase();
      if (!this.allowedStyleProperties.has(name)) {
        return null;
      }
      const value = valueParts.join(':').trim();
      if (!this.isSafeStyleValue(value)) {
        return [];
      }
      return options.inlineMarkRewrite
        ? rewriteContentDeclaration(name, value, { tagName: options.tagName })
        : [`${name}: ${value}`];
    }).filter((dec): dec is string => Boolean(dec));

    return safeDeclarations.join('; ');
  }

  private isSafeStyleValue(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    if (normalized.includes('javascript:')) {
      return false;
    }
    if (normalized.includes('expression(')) {
      return false;
    }
    if (normalized.includes('url(')) {
      return false;
    }
    return true;
  }

  private findMatchingBrace(input: string, start: number): number {
    let depth = 0;
    for (let i = start; i < input.length; i++) {
      const char = input[i];
      if (char === '{') {
        depth += 1;
      } else if (char === '}') {
        depth -= 1;
        if (depth === 0) {
          return i;
        }
      }
    }
    return -1;
  }

  private isSafeUrl(raw: string, allowDataImage: boolean): boolean {
    const value = raw.trim();
    if (!value || /^javascript:/i.test(value)) {
      return false;
    }
    if (allowDataImage && /^data:image\//i.test(value)) {
      return true;
    }
    try {
      const parsed = new URL(value, this.document.location?.href);
      return ['http:', 'https:', 'mailto:'].includes(parsed.protocol);
    } catch (_) {
      return false;
    }
  }

  private isScrolledToEnd(area: HTMLElement): boolean {
    const threshold = 12;
    return area.scrollTop + area.clientHeight >= area.scrollHeight - threshold;
  }

  private startScrollHintBounce(hint: HTMLElement): void {
    const bounceClass = `${this.P}hint-bounce`;
    const targets = [
      hint.querySelector(`.${this.P}scroll-label`),
      hint.querySelector('svg')
    ].filter((el): el is HTMLElement => Boolean(el));

    targets.forEach(el => el.classList.add(bounceClass));
    window.setTimeout(() => {
      targets.forEach(el => el.classList.remove(bounceClass));
    }, 3000);
  }

  private applyLogoCustomization(dialog: DialogElements, customizations?: ClickwrapCustomizationSettingsResult): void {
    const isLogoCustomizable = customizations?.isLogoCustomizable ?? false;
    const logoUrl = customizations?.logoUrl?.trim();

    if (!isLogoCustomizable) {
      this.showDefaultBrand(dialog);
      return;
    }

    if (!logoUrl) {
      this.hideBrand(dialog);
      return;
    }

    if (!this.isSafeUrl(logoUrl, true)) {
      this.hideBrand(dialog);
      return;
    }

    this.showCustomBrand(dialog, logoUrl);
  }

  private hideBrand(dialog: DialogElements): void {
    dialog.brand.hidden = true;
    dialog.brand.classList.add('is-hidden');
    dialog.brand.classList.remove('is-custom');
    dialog.brand.removeAttribute('href');
    dialog.brand.removeAttribute('target');
    dialog.brand.removeAttribute('rel');
    dialog.brand.removeAttribute('aria-label');
    dialog.brandLogo.removeAttribute('src');
    dialog.brandLogo.removeAttribute('alt');
  }

  private showDefaultBrand(dialog: DialogElements): void {
    dialog.brand.hidden = false;
    dialog.brand.classList.remove('is-hidden');
    dialog.brand.classList.remove('is-custom');
    dialog.brand.setAttribute('href', buildBrandHref(this.appId));
    dialog.brand.setAttribute('target', '_blank');
    dialog.brand.setAttribute('rel', 'noopener');
    dialog.brand.setAttribute('aria-label', 'Powered by ClickTerm');
    dialog.brandLogo.removeAttribute('src');
    dialog.brandLogo.removeAttribute('alt');
  }

  private showCustomBrand(dialog: DialogElements, logoUrl: string): void {
    dialog.brand.hidden = false;
    dialog.brand.classList.remove('is-hidden');
    dialog.brand.classList.add('is-custom');
    dialog.brand.removeAttribute('href');
    dialog.brand.removeAttribute('target');
    dialog.brand.removeAttribute('rel');
    dialog.brand.setAttribute('aria-label', 'Brand logo');
    dialog.brandLogo.src = logoUrl;
    dialog.brandLogo.alt = 'Brand logo';
  }

  private applyThemeCustomizations(modal: HTMLElement, customizations?: ClickwrapCustomizationSettingsResult): void {
    if (!customizations) {
      return;
    }
    const themeColors = customizations.themeColors;
    const buttonSettings = customizations.buttonSettings;
    const variables: string[] = [];

    const addVar = (name: string, value?: string | null) => {
      const safeValue = sanitizeCssValue(value);
      if (safeValue) {
        variables.push(`${name}: ${safeValue}`);
      }
    };

    addVar('--ct-w-primary-bg', themeColors?.primaryButtonColor);
    addVar('--ct-w-primary-bg-hover', themeColors?.primaryButtonColor);
    addVar('--ct-w-primary-text', themeColors?.primaryTextColor);
    addVar('--ct-w-secondary-bg', themeColors?.secondaryButtonColor);
    addVar('--ct-w-secondary-bg-hover', themeColors?.secondaryButtonColor);
    addVar('--ct-w-secondary-text', themeColors?.secondaryTextColor);

    addVar('--ct-w-primary-border', buttonSettings?.primaryButtonBorderColor);
    addVar('--ct-w-primary-border-hover', buttonSettings?.primaryButtonBorderColor);
    addVar('--ct-w-secondary-border', buttonSettings?.secondaryButtonBorderColor);
    addVar('--ct-w-secondary-border-hover', buttonSettings?.secondaryButtonBorderColor);

    if (typeof buttonSettings?.buttonBorderRadius === 'number' && Number.isFinite(buttonSettings.buttonBorderRadius)) {
      const radius = Math.max(0, buttonSettings.buttonBorderRadius);
      variables.push(`--ct-w-button-radius: ${radius}px`);
    }

    if (!variables.length) {
      return;
    }
    const themeId = `ct-w-theme-${Math.random().toString(36).slice(2, 10)}`;
    modal.setAttribute('data-ct-w-theme', themeId);
    const style = this.document.createElement('style');
    style.textContent = `[data-ct-w-theme="${themeId}"] { ${variables.join('; ')}; }`;
    modal.appendChild(style);
  }

}

export interface ModalRef {
  root: HTMLElement;
  close(): Promise<void>;
}

export interface InlineRef {
  root: HTMLElement;
  checkbox: HTMLInputElement;
  setLocked(locked: boolean): void;
}

interface DialogElements {
  fragment: DocumentFragment;
  dialog: HTMLElement;
  title: HTMLElement;
  subtitle: HTMLElement | null;
  brand: HTMLAnchorElement;
  brandLogo: HTMLImageElement;
  scrollArea: HTMLElement;
  scrollContent: HTMLElement;
  scrollHint: HTMLElement;
  scrollLabel: HTMLElement;
  infoBanner: HTMLElement;
  infoBannerText: HTMLElement;
  bannerLegal: HTMLElement;
  ackRow: HTMLDivElement;
  ackLegal: HTMLElement;
  ackField: HTMLDivElement;
  ackInput: HTMLInputElement;
  ackLabel: HTMLElement;
  accept: HTMLButtonElement;
  decline: HTMLButtonElement;
}

interface ModalRenderOptions {
  renderInlineAcknowledgement?: boolean;
}

interface ReadOnlyModalOptions {
  subtitleMode?: 'accepted' | 'published';
}

export interface LinkModalContext {
  translations: ClicktermDialogTranslations;
  customizations?: ClickwrapCustomizationSettingsResult;
}

interface InlineTemplateElements {
  fragment: DocumentFragment;
  root: HTMLElement;
  checkboxInput: HTMLInputElement;
  content: HTMLElement;
}

interface ContentSanitizationOptions {
  scopeSelector?: string;
  inlineMarkRewrite?: boolean;
}

interface DeclarationSanitizationOptions {
  inlineMarkRewrite?: boolean;
  tagName?: string;
}
